
/**
 * Exercises Integration Layer
 * Connects SDK Service to Zustand Store
 */
import { toast } from 'sonner';
import { useExercisesStore } from '../../store/exercises.store.js';
import i18next from 'i18next';
import { exercisesApi } from './exercises.api.js';
import { createTeamAssociationIntegrationMethods } from './team-association.integration-mixin.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { bumpHubEntityVersion } from '@/lib/reference-sync/bump-hub-entity.js';

export class ExercisesIntegration {
    constructor(sdkService) {
        this.service = sdkService;
        Object.assign(this, createTeamAssociationIntegrationMethods({
            afterMutate: this._afterExerciseAssociationMutation.bind(this),
        }));
    }

    _patchSelectedExerciseIfCurrent(exerciseId, data) {
        if (!exerciseId || !data) return;
        const store = useExercisesStore.getState();
        const current = store.selectedExercise;
        const currentId = current?.id || current?._id;
        if (currentId && String(currentId) === String(exerciseId) && store.setSelectedExercise) {
            store.setSelectedExercise(data);
        }
    }

    _afterExerciseAssociationMutation(exerciseId, data) {
        this._patchSelectedExerciseIfCurrent(exerciseId, data);
        bumpHubEntityVersion('exercise', 'exercises.association');
    }

    /**
     * Fetch Exercises
     */
    async fetchExercises(page = 1, pageSize = 10, search = '', sortBy = 'name', sortOrder = 'asc', filters = {}) {
        return this._fetchItems({ page, pageSize, search, sortBy, sortOrder, ...filters }, 'EXERCISES');
    }

    /**
     * Fetch Single Exercise by ID
     */
    async fetchExerciseById(id, silent = false) {
        const store = useExercisesStore.getState();
        if (!silent) store.setLoadingDetail(true);
        if (store.setErrorDetail) store.setErrorDetail(null);

        try {
            const response = await this.service.getById(id);
            store.setSelectedExercise(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setErrorDetail) store.setErrorDetail(message);
            if (!silent) toast.error(message);
            return { success: false, error: message };
        } finally {
            if (!silent) store.setLoadingDetail(false);
        }
    }

    /**
     * Fetch Improvement Actions
     */
    async fetchImprovementActions() {
        return { success: false, error: 'Use ExerciseImprovementActionsIntegration' };
    }

    async _fetchItems(params, type) {
        const store = useExercisesStore.getState();

        let setLoading, setList, setPagination;

        if (type === 'EXERCISES') {
            setLoading = store.setLoadingExercises;
            setList = store.setExercises;
            setPagination = store.setPaginationExercises;
        } else if (type === 'IMPROVEMENT') {
            setLoading = store.setLoadingImprovement;
            setList = store.setImprovementActions;
            setPagination = store.setPaginationImprovement;
        }

        if (setLoading) setLoading(true);
        if (store.clearError) store.clearError();

        try {
            const response = await this.service.getAll(params);

            if (setList) setList(response.data);
            store.setUniqueValues?.(response.uniqueValues || {});

            const pagination = response.pagination || response.meta;
            if (pagination && setPagination) {
                const currentPage = parseInt(pagination.page || pagination.currentPage || 1);
                const totalPages = parseInt(pagination.totalPages || 1);
                setPagination({
                    currentPage: currentPage,
                    itemsPerPage: parseInt(pagination.pageSize || pagination.itemsPerPage || 10),
                    totalItems: parseInt(pagination.total || pagination.totalItems || 0),
                    totalPages: totalPages,
                    hasNextPage: currentPage < totalPages,
                    hasPreviousPage: currentPage > 1
                });
            }

            return { success: true, data: response.data, uniqueValues: response.uniqueValues || {} };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (setLoading) setLoading(false);
        }
    }

    /**
     * Fetch Stats
     */
    async fetchStats() {
        const store = useExercisesStore.getState();
        if (store.setLoadingStats) store.setLoadingStats(true);

        try {
            // Check if service has getStats, else fallback
            const response = this.service.getStats ? await this.service.getStats() : { data: null };
            if (store.setStats) store.setStats(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: error.message };
        } finally {
            if (store.setLoadingStats) store.setLoadingStats(false);
        }
    }

    /**
     * CRUD Operations
     */
    async createExercise(data) {
        return this._execute(async () => await this.service.create(data), i18next.t('bcmExercise:messages.createSuccess', { defaultValue: 'Exercise created successfully' }));
    }

    async updateExercise(id, data) {
        return this._execute(async () => await this.service.update(id, data), i18next.t('bcmExercise:messages.updateSuccess', { defaultValue: 'Exercise updated successfully' }));
    }

    async deleteExercise(id) {
        return this._execute(async () => await this.service.delete(id), i18next.t('bcmExercise:messages.deleteSuccess', { defaultValue: 'Exercise deleted successfully' }));
    }

    async bulkDeleteExercises(ids) {
        return this._executeBatch(async () => await this.service.batchDelete(ids));
    }

    // ===================== Related BCPs =====================
    async fetchRelatedBcps(exerciseId, params = {}) {
        try {
            const response = await this.service.getRelatedBcps(exerciseId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async addBcps(exerciseId, bcpIds, opts = {}) {
        try {
            const response = await this.service.addBcps(exerciseId, bcpIds);
            if (!opts.silent) {
                toast.success(i18next.t('bcmExercise:messages.bcpsLinked', { defaultValue: 'BCPs linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (!opts.silent) toast.error(message);
            return { success: false, error: message };
        }
    }
    async removeBcp(exerciseId, bcpId) {
        try {
            const response = await this.service.removeBcp(exerciseId, bcpId);
            toast.success(i18next.t('bcmExercise:messages.bcpUnlinked', { defaultValue: 'BCP link removed' }));
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }

    // ===================== Participating Vendors =====================
    async fetchRelatedVendors(exerciseId, params = {}) {
        try {
            const response = await this.service.getRelatedVendors(exerciseId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async addVendors(exerciseId, vendorIds, opts = {}) {
        try {
            const response = await this.service.addVendors(exerciseId, vendorIds);
            if (!opts.silent) {
                toast.success(i18next.t('bcmExercise:messages.vendorsLinked', { defaultValue: 'Vendors linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (!opts.silent) toast.error(message);
            return { success: false, error: message };
        }
    }
    async removeVendor(exerciseId, vendorId) {
        try {
            const response = await this.service.removeVendor(exerciseId, vendorId);
            toast.success(i18next.t('bcmExercise:messages.vendorUnlinked', { defaultValue: 'Vendor link removed' }));
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }

    // ===================== Exercise Participants =====================
    async fetchRelatedParticipants(exerciseId, params = {}) {
        try {
            const response = await this.service.getRelatedParticipants(exerciseId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async addParticipants(exerciseId, participantIds) {
        try {
            const response = await this.service.addParticipants(exerciseId, participantIds);
            toast.success(i18next.t('bcmExercise:messages.participantsLinked', { defaultValue: 'Participants linked successfully' }));
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async removeParticipant(exerciseId, participantId) {
        try {
            const response = await this.service.removeParticipant(exerciseId, participantId);
            toast.success(i18next.t('bcmExercise:messages.participantUnlinked', { defaultValue: 'Participant link removed' }));
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }

    // ===================== Related Assessments =====================
    async fetchRelatedAssessments(exerciseId, params = {}) {
        try {
            const response = await this.service.getRelatedAssessments(exerciseId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async addAssessments(exerciseId, assessmentIds) {
        try {
            const response = await this.service.addAssessments(exerciseId, assessmentIds);
            toast.success(i18next.t('bcmExercise:messages.assessmentsLinked', { defaultValue: 'Assessments linked successfully' }));
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async removeAssessment(exerciseId, assessmentId) {
        try {
            const response = await this.service.removeAssessment(exerciseId, assessmentId);
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }

    // ===================== Scenario Timelines =====================
    async fetchRelatedTimelines(exerciseId, params = {}) {
        try {
            const response = await this.service.getRelatedTimelines(exerciseId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async addTimelines(exerciseId, ids, isSilent = false) {
        try {
            const response = await this.service.addTimelines(exerciseId, { timelineIds: ids, silent: isSilent });
            if (!isSilent) toast.success(i18next.t('bcmExercise:messages.timelinesLinked', { defaultValue: 'Timelines linked successfully' }));
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async removeTimeline(exerciseId, timelineId) {
        try {
            const response = await this.service.removeTimeline(exerciseId, timelineId);
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }

    // ===================== Recovery Activities =====================
    async fetchRelatedRecoveryActivities(exerciseId, params = {}) {
        try {
            const response = await this.service.getRelatedRecoveryActivities(exerciseId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async addRecoveryActivities(exerciseId, activityIds) {
        try {
            const response = await this.service.addRecoveryActivities(exerciseId, activityIds);
            toast.success(i18next.t('bcmExercise:messages.recoveryActivitiesLinked', { defaultValue: 'Recovery activities linked successfully' }));
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async removeRecoveryActivity(exerciseId, activityId) {
        try {
            const response = await this.service.removeRecoveryActivity(exerciseId, activityId);
            toast.success(i18next.t('bcmExercise:messages.recoveryActivityUnlinked', { defaultValue: 'Recovery activity link removed' }));
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async reorderRecoveryActivities(exerciseId, orderedActivityIds) {
        try {
            const response = await this.service.reorderRecoveryActivities(exerciseId, orderedActivityIds);
            toast.success(i18next.t('bcmExercise:messages.recoveryActivitiesReordered', { defaultValue: 'Recovery activities reordered' }));
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }

    // ===================== Exercise Evaluations =====================
    async fetchRelatedEvaluations(exerciseId, params = {}) {
        try {
            const response = await this.service.getRelatedEvaluations(exerciseId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async addEvaluations(exerciseId, evaluationIds) {
        try {
            const response = await this.service.addEvaluations(exerciseId, evaluationIds);
            toast.success(i18next.t('bcmExercise:messages.evaluationsLinked', { defaultValue: 'Evaluations linked successfully' }));
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async removeEvaluation(exerciseId, evaluationId) {
        try {
            const response = await this.service.removeEvaluation(exerciseId, evaluationId);
            toast.success(i18next.t('bcmExercise:messages.evaluationUnlinked', { defaultValue: 'Evaluation link removed' }));
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }

    // ===================== Improvement Actions =====================
    async fetchRelatedImprovementActions(exerciseId, params = {}) {
        try {
            const response = await this.service.getRelatedImprovementActions(exerciseId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async addImprovementActions(exerciseId, actionIds, extra = {}) {
        try {
            const response = await this.service.addImprovementActions(exerciseId, actionIds, extra);
            toast.success(i18next.t('bcmExercise:messages.improvementAction.linked', { defaultValue: 'Improvement actions linked successfully' }));
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async getLinkableImprovementActions(exerciseId, params = {}) {
        try {
            const response = await this.service.getLinkableImprovementActions(exerciseId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, data: [] }; }
    }
    async unlinkImprovementActions(exerciseId, actionIds, opts = {}) {
        try {
            const response = await this.service.unlinkImprovementActions(exerciseId, actionIds);
            if (!opts.silent) {
                toast.success(i18next.t('bcmExercise:messages.improvementAction.unlinked', { defaultValue: 'Improvement action unlinked' }));
            }
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async removeImprovementAction(exerciseId, actionId, opts = {}) {
        try {
            const response = await this.service.removeImprovementAction(exerciseId, actionId);
            if (!opts.silent) {
                toast.success(i18next.t('bcmExercise:messages.improvementAction.unlinked', { defaultValue: 'Improvement action unlinked' }));
            }
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }

    /**
     * Helper to execute operations
     */
    async _execute(operation, successMessage) {
        const store = useExercisesStore.getState();
        if (store.setIsSubmitting) store.setIsSubmitting(true);

        try {
            const response = await operation();
            toast.success(successMessage);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setIsSubmitting) store.setIsSubmitting(false);
        }
    }

    /**
     * Helper for batch operations
     */
    async _executeBatch(operation) {
        const store = useExercisesStore.getState();
        if (store.setIsSubmitting) store.setIsSubmitting(true);

        try {
            const response = await operation();
            const count = response.deletedCount ?? response.data?.deletedCount;
            const msg = count !== undefined
                ? i18next.t('bcmExercise:messages.bulkDeleteSuccess', {
                    count,
                    defaultValue: `${count} exercises deleted successfully`,
                })
                : i18next.t('bcmExercise:messages.deleteSuccess', { defaultValue: 'Items deleted' });
            toast.success(msg);
            return { success: true, deletedCount: count };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setIsSubmitting) store.setIsSubmitting(false);
        }
    }

    async getOptions() {
        try {
            const options = await exercisesApi.getOptions();
            return { success: true, data: options };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchExerciseParty(exerciseId) {
        try {
            const response = await this.service.getParty(exerciseId);
            return {
                success: response.success !== false,
                data: response.data,
                partyTemplate: response.partyTemplate ?? null,
            };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, data: null, partyTemplate: null };
        }
    }

    async syncExerciseParty(exerciseId, payload, options = {}) {
        const { silentSuccess = false } = options;
        try {
            const response = await this.service.syncParty(exerciseId, payload);
            if (!silentSuccess) {
                toast.success(i18next.t('bcmExercise:messages.partySaveSuccess', { defaultValue: 'Ownership and roles saved' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false };
        }
    }

    async fetchRelatedLessonsLearned(exerciseId, params = {}) {
        try {
            const response = await this.service.getRelatedLessonsLearned(exerciseId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, data: [], pagination: {} };
        }
    }

    async addLessonsLearned(exerciseId, lessonIds) {
        try {
            const response = await this.service.addLessonsLearned(exerciseId, lessonIds);
            this._afterExerciseAssociationMutation(exerciseId, response.data);
            toast.success(i18next.t('bcmExercise:messages.lessonsLinked', { defaultValue: 'Lessons linked' }));
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false };
        }
    }

    async removeLessonLearned(exerciseId, lessonId, opts = {}) {
        try {
            const response = await this.service.removeLessonLearned(exerciseId, lessonId);
            this._afterExerciseAssociationMutation(exerciseId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('bcmExercise:messages.lessonUnlinked', { defaultValue: 'Lesson link removed' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
